// dependencies
import produce from "immer";
import { isImmutable } from "immutable";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
	faCheckCircle,
	faChevronDown,
	faChevronRight,
	faChevronUp,
	faExclamationCircle,
	faInfoCircle,
	faTimesCircle,
	faUserTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Avatar } from "@performyard/py-components";

import DownloadButton from "../../../components.smart/DownloadButton";
import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Button from "../../../components/Button";
import Chip from "../../../components/Chip";
import PageTitle from "../../../components/PageTitle";
import Paginator from "../../../components/Pagination";
import TableLite from "../../../components/TableLite";
import BulkDeactivateModal from "./BulkDeactivateModal";
// components
import Filters from "./Filters";
import Search from "./Search";

// hooks
import useRequest from "../../../hooks/useRequest";

import { getUserCompanyName } from "../../../selectors/app";
// selectors
import { getURLParam } from "../../../selectors/navigation";
import { selectSyncInformation } from "./selectors";

// utils
import get from "../../../utils/GetNested";
import toEmployeeUrl from "../../../utils/URLs/Employee";

import ErrorPage from "../../Error";
// styles
import css from "./LogDetails.module.scss";
// config
import config from "./config";

/**
 * Before the request is sent, transform some of the filters so the server
 * can process them.
 */
const transformQuery = (query) => {
	const updatedQuery = produce(query, (draftState) => {
		const status = get(draftState, ["filters", "userUpdatesStatus"], null);

		// if status is all, then there really isn't a filter for
		// status, so remove it.
		if (status === "all") {
			delete draftState.filters.userUpdatesStatus;
		}

		// parse the other keys
		Object.keys(draftState.filters).forEach((key) => {
			const value = draftState.filters[key];
			if (isImmutable(value)) {
				if (key === "employee") {
					draftState.filters[key] = value.get("id", "");
				}
			}
		});
	});

	return updatedQuery;
};

const LogDetails = () => {
	const [logData, setLogData] = useState({});
	const [currentPage, setCurrentPage] = useState(1);
	const [openErrorCells, updateErrorCells] = useImmer({});
	const [displayBulkDeactivateModal, setDisplayBulkDeactivateModal] = useState(false);
	const [updatedDetailsShowing, setUpdatedDetailsShowing] = useState({});
	const [duplicateEmailsExpanded, setDuplicateEmailsExpanded] = useState(false);

	// state and setter for filters
	const [filters, updateFilters] = useImmer({
		userUpdatesStatus: "all",
	});

	const [search, setSearch] = useState({});

	const handleFilterChange = (filterChange) => {
		updateFilters(filterChange);
		setCurrentPage(1);
	};

	const handleSearch = (newSearch) => {
		if (newSearch) {
			setSearch({ search: newSearch });
		} else {
			setSearch({});
		}
		setCurrentPage(1);
	};

	const PAGE_SIZE = config.pageSize;
	const limit = PAGE_SIZE;
	const skip = (currentPage - 1) * limit;

	const navigate = useNavigate();
	const logID = useSelector((state) => getURLParam(state, "integrationId"));

	const {
		data,
		isRequesting,
		revalidate: getLog,
	} = useRequest(
		"getLog",
		{
			filters: {
				...search,
				...filters,
				integrationLogID: logID,
				userUpdatesSkip: skip,
				userUpdatesLimit: limit,
			},
		},
		{
			onBeforeRequest: transformQuery,
			ignoreFailed: true,
			skipCache: true,
		}
	);

	const { data: csvExists } = useRequest(
		"checkCsvExists",
		{
			filters: { integrationLogID: logID },
		},
		{
			ignoreFailed: true,
		}
	);

	const { revalidate: getSignedURL } = useRequest(
		"getSignedURL",
		{
			filters: { integrationLogID: logID },
		},
		{
			manual: true,
		}
	);

	const { revalidate: bulkDeactivateUsers, isRequesting: deactivatingUsers } = useRequest(
		"BulkDeactivateUsers",
		{},
		{
			manual: true,
		}
	);

	const { revalidate: dismissDeactivateUsers, isRequesting: dismissingUsers } = useRequest(
		"dismissDeactivateUsers",
		{},
		{
			manual: true,
		}
	);

	const { data: employeeData, revalidate: getDeactivatedEmployeeNames } = useRequest(
		"GetDeactivatedEmployeeNames",
		{ filters: { integrationLogID: logID } },
		{
			manual: true,
		}
	);

	const { data: employeesRequiringApprovalToDeactivate, revalidate: getEmployeesRequiringApprovalToDeactivate } =
		useRequest(
			"GetEmployeesRequiringApprovalToDeactivate",
			{ filters: { integrationLogID: logID } },
			{
				manual: true,
			}
		);

	// selectors
	const company = useSelector(getUserCompanyName);
	const totalUsersUpdates = get(logData, ["total_user_updates"], "0");
	const noUpdates = get(logData, ["users_with_no_changes"], 0);
	const duplicateEmployeeEmails = get(logData, ["duplicate_employee_emails"], []);
	const updated = get(logData, ["users_successfully_synced"], 0);
	const failed = get(logData, ["users_failed_sync"], 0);
	const skipped = noUpdates > 0 && logData.errors.length === 0;
	const testingMode = get(logData, ["sftp_properties", "testing_mode"], false);
	const usersToDeactivate = get(logData, ["requires_approval_to_deactivate"], []);
	const numUsersToDeactivate = usersToDeactivate ? usersToDeactivate.length : 0;
	const usersDeactivationApproved = get(logData, ["user_deactivation_approved"], false);
	const usersDeactivationDismissed = get(logData, ["user_deactivation_dismissed"], false);
	const usersDeactivated = get(logData, ["users_deactivated"], []);
	const isProcessing = get(logData, ["is_processing"], false);
	const completedAt = isProcessing
		? "Processing"
		: moment(get(logData, ["finished_at"], "")).format("MMMM Do YYYY HH:mm:ss");

	const getEmployeesInCsv = (integrationType) => {
		switch (integrationType) {
			case "integration-sync":
				return get(logData, ["integrationProperties", "connectedEmployeesCount"], "");
			case "connect-workers":
				return get(logData, ["connect_worker_properties", "numUsersAttemptedToSync"], "");
			default:
				return get(logData, ["csv_properties", "num_users_in_csv"], "");
		}
	};

	const employeesInCsv = getEmployeesInCsv(logData?.integration_type);

	useEffect(() => {
		if (data) {
			setLogData(data.integrationLogs[0]);
		}
	}, [data]);

	const handleDeactivation = async () => {
		const response = await bulkDeactivateUsers({
			filters: {
				listOfUsersToDeactivate: get(logData, ["requires_approval_to_deactivate"], []),
				logId: logID,
			},
		});
		getDeactivatedEmployeeNames();
		getLog();
		return response;
	};

	const handleDismiss = async () => {
		const response = await dismissDeactivateUsers({
			filters: {
				logId: logID,
			},
		});
		getLog();
		return response;
	};

	/**
	 * When there aren't any user updates to show,
	 * integrationLogs[0].user_updates is [{}]
	 * we remove all empty objects, so that .map returns and empty array
	 */
	const userUpdatesToProcess = get(data, ["integrationLogs", 0, "user_updates"], []).filter(
		(item) => Object.keys(item).length !== 0
	);

	const userUpdates = userUpdatesToProcess.map((user) => ({
		id: user.userID,
		error: user.error,
		hasError: user.hasError,
		insertedCount: user.insertedCount,
		updatedFields: user.updatedFields,
		updatesFound: user.updatesFound,
		created: user.created,
		userObject: user.userObject,
	}));

	const handleShowErrorCell = (cellID, nextState) => {
		updateErrorCells((draft) => {
			draft[cellID] = nextState;
		});
	};

	const handleToggleUpdatedDetails = (userID) => {
		const detailsShowing = { ...updatedDetailsShowing };
		detailsShowing[userID] = !detailsShowing[userID];
		setUpdatedDetailsShowing(detailsShowing);
	};

	const allOpen = () => {
		const keys = Object.keys(updatedDetailsShowing);
		const fullLength = userUpdates.filter((userRow) => userRow.updatesFound).length;
		if (keys.length < fullLength) return false;
		let openness = true;
		keys.forEach((detail) => {
			if (!updatedDetailsShowing[detail]) {
				openness = false;
			}
		});
		return openness;
	};

	const handleToggleAll = () => {
		const flip = !allOpen();
		const newUpdated = userUpdates.reduce((updates, userRow) => {
			if (userRow.updatesFound) {
				updates[userRow.id] = flip;
			}
			return updates;
		}, {});
		setUpdatedDetailsShowing(newUpdated);
	};

	const oneLink = (value) => {
		if (get(value, ["link"], "")) {
			return (
				<a key={value.link} href={value.link}>
					{get(value, ["name"], "Other User")}
				</a>
			);
		}
		// if link is falsey, render only the name
		// ( or a 'Other User' placeholder)
		return get(value, ["name"], "Other User");
	};

	const semiColonize = (linkOrStringMap) =>
		linkOrStringMap.map((field, index) => {
			if (index === linkOrStringMap.length - 1) {
				// final entry shall have no semicolon appended
				return field;
			}
			// all other values shall have a semicolon appended
			return <span key={`sub-${field.href ? field.href : field}`}>{field}; </span>;
		});

	const renderFieldValue = (value) => {
		let rendered = "";
		switch (typeof value) {
			case "string":
				rendered = value;
				break;
			case "number":
				rendered = value.toString();
				break;
			case "object":
				if (!Array.isArray(value)) {
					rendered = oneLink(value);
				} else if (Array.isArray(value)) {
					// create link or string map
					const preMapped = value.map((val) => (typeof val === "object" ? oneLink(val) : val));
					// render seperated by semicolons
					rendered = semiColonize(preMapped);
				}
				break;
			default:
				break;
		}
		return rendered || <span style={{ fontStyle: "italic" }}>Cleared</span>;
	};

	const detailsColumnLabel = (
		<div className={css.detailsColumnHeader}>
			<span>Details</span>
			{userUpdatesToProcess.find((update) => update.updatesFound) ? (
				<button className={`${css.showMoreButton} ${css.columnHeader}`} onClick={() => handleToggleAll()}>
					{allOpen() ? (
						<span key="collapse" title="Hide All">
							<FontAwesomeIcon icon={faChevronUp} />
						</span>
					) : (
						<span key="expand" title="Show All">
							<FontAwesomeIcon icon={faChevronDown} />
						</span>
					)}
				</button>
			) : null}
		</div>
	);

	const buildExpandedDetailsButton = (userID) => (
		<button onClick={() => handleToggleUpdatedDetails(userID)} className={css.showMoreButton}>
			{updatedDetailsShowing[userID] ? (
				<span key={`${userID}-hide-details`} title="Hide Details">
					Show Less
				</span>
			) : (
				<span key={`${userID}-show-details`} title="Show Details">
					Show More
				</span>
			)}
		</button>
	);

	const buildExpandedDetails = (user) => (
		<>
			{user.updatedFields.updatedFields.map((field) => (
				<div key={field.fieldName} className={css.updatedDetails}>
					<span className={css.fieldName}>{field.fieldName}</span>: {renderFieldValue(field.value)}
				</div>
			))}
		</>
	);

	const buildDetailsCell = (user) => (
		<>
			<div className={css.detailsColumnCell}>
				<span>Updated {user.insertedCount} items</span>
				{
					// if there are updates make them expandable
					!user.updatesFound ? null : buildExpandedDetailsButton(user.id)
				}
			</div>
			{!updatedDetailsShowing[user.id] ? null : buildExpandedDetails(user)}
		</>
	);

	const userIsDeactivated = (user) => {
		return usersDeactivated.some((deactivatedUser) => deactivatedUser === user.id);
	};

	const getStatus = (user) => {
		if (user.hasError) {
			return <Chip label="Skipped" color="yellow" />;
		} else if (user.created) {
			return <Chip label="Created" color="green" />;
		} else if (userIsDeactivated(user)) {
			return <Chip label="Deactivated" color="orange" />;
		} else if (user.updatesFound) {
			return <Chip label="Synced" color="green" />;
		} else if (!user.hasError && !user.updatesFound) {
			return <Chip label="No Updates" color="blue" />;
		}
		return "";
	};

	const columns = [
		{
			id: "userID",
			label: "Employee",
			width: "230px",
			isSortable: false,
			getter: (user) => {
				let userName = get(user, ["userObject", "name"], "");
				const userID = get(user, ["userObject", "_id"], "");
				// If no userID, then this is a testmode/simulated user, try to get their name from the updatedFields
				if (userID === "") {
					const userUpdates = get(user, ["updatedFields", "updatedFields"], []);
					const email = userUpdates.find((field) => field.fieldName === "Email")?.value;
					const lastName = userUpdates.find((field) => field.fieldName === "Last Name")?.value;
					const firstName = userUpdates.find((field) => field.fieldName === "First Name")?.value;
					const nickName = userUpdates.find((field) => field.fieldName === "Nickname")?.value;

					if (lastName && (nickName || firstName)) {
						userName = `${nickName || firstName} ${lastName}`;
					} else if (email) {
						userName = email;
					} else {
						userName = user.created;
					}
				}

				return (
					<div className={css.userBox}>
						<Avatar size={20} dangerouslySetClassName={css.userAvatar} id={user.id} />
						{userID === "" ? (
							<div className={css.userName}>{userName}</div>
						) : (
							<div className={css.userName}>
								<a href={toEmployeeUrl(userID)}>{userName || ""}</a>
							</div>
						)}
					</div>
				);
			},
		},
		{
			id: "status",
			label: "Status",
			width: "120px",
			isSortable: false,
			getter: (user) => <div>{getStatus(user)}</div>,
		},
		{
			id: "details",
			label: detailsColumnLabel,
			isSortable: false,
			getter: (user) => {
				const { hasError, id: userID } = user;

				if (!hasError) {
					return buildDetailsCell(user);
				}

				if (user.error.length === 1) {
					return <div>{user.error[0].message}</div>;
				}

				const isOpen = get(openErrorCells, [userID], false);

				if (isOpen) {
					return (
						<div>
							{user.error.map((error) => (
								<div key={error.message}>{error.message}</div>
							))}

							<Button
								size="micro"
								onClick={() => handleShowErrorCell(userID, !isOpen)}
								className={css.hideErrorsButton}
							>
								Hide Errors
							</Button>
						</div>
					);
				}

				return (
					<div>
						<div>
							There were {user.error.length} errors while processing this employee.{" "}
							<Button
								size="micro"
								onClick={() => handleShowErrorCell(userID, !isOpen)}
								className={css.showErrorsButton}
							>
								Show Errors
							</Button>
						</div>
					</div>
				);
			},
		},
	];

	const handleChangePage = (nextPage) => {
		setCurrentPage(nextPage);
	};

	const handleDownloadCsv = async () => {
		const signedURL = await getSignedURL();
		if (signedURL.data) {
			window.location.href = signedURL.data;
		}
	};

	const handleBackButton = () => {
		navigate(-1);
	};

	return (
		<AdminContainer>
			<AdminTitle>Sync Details</AdminTitle>
			{testingMode ? <Chip label="Testing Mode" color="orange" /> : null}

			<div>
				<PageTitle title={`Integration Log Details - ${company} - PerformYard`} />

				<div className={css.logDataContainer}>
					<Button style={{ marginBottom: "20px", height: "32px" }} onClick={handleBackButton}>
						{" "}
						Back To Log
					</Button>

					<div>
						<div>
							<div className={css.integrationDetail}>
								<div>Sync Information:</div>
								<div>{selectSyncInformation(logData)}</div>
							</div>
							<div className={css.integrationDetail}>
								<div>Completed At</div>
								<div>{completedAt}</div>
							</div>
							<div className={css.integrationDetail}>
								<div>Total Employees</div>
								<div>{employeesInCsv}</div>
							</div>
							{csvExists && csvExists.csvExists === true ? (
								<div className={css.downloadButton}>
									<DownloadButton
										isHollow={true}
										onClick={handleDownloadCsv}
										style={{
											height: "32px",
											marginBottom: "20px",
										}}
										isJobRunning={false}
									>
										Download CSV
									</DownloadButton>
								</div>
							) : null}
							{!usersDeactivationApproved && !usersDeactivationDismissed && numUsersToDeactivate > 0 ? (
								<div>
									<div className={css.deactivateDescription}>
										We detected a large number of users that would be deactivated by this sync. This
										requires additional action.
									</div>
									<Button
										style={{
											marginBottom: "20px",
											height: "32px",
										}}
										color="red"
										onClick={() => setDisplayBulkDeactivateModal(true)}
									>{`Deactivate (${numUsersToDeactivate} user${
										numUsersToDeactivate > 1 ? "s" : ""
									})`}</Button>
								</div>
							) : null}
						</div>
						<div className={css.syncData}>
							<div className={css.syncSummaries}>
								{duplicateEmployeeEmails?.length > 0 ? (
									<div className="mb-5 mr-2.5 rounded-sm bg-yellow-50 p-5">
										<div
											className="duration-20 -m-1.5 flex cursor-pointer flex-row rounded-sm p-1.5 font-semibold transition-colors"
											onClick={() => setDuplicateEmailsExpanded(!duplicateEmailsExpanded)}
										>
											<div className="mr-1.5 text-lg text-yellow-600">
												<FontAwesomeIcon icon={faExclamationCircle} />
											</div>
											<div>
												{`${duplicateEmployeeEmails.length} employees were skipped due to PerformYard receiving multiple employee records representing the same person. Please update your integration mapping settings to select the correct employee records to use.`}
											</div>
											<div className="ml-auto flex items-center text-sm text-neutral-400">
												<FontAwesomeIcon
													icon={duplicateEmailsExpanded ? faChevronUp : faChevronDown}
												/>
											</div>
										</div>
										{duplicateEmailsExpanded && (
											<ul className="ml-4 mt-2">
												{duplicateEmployeeEmails.map((email) => (
													<li key={email}>{email}</li>
												))}
											</ul>
										)}
									</div>
								) : null}

								{usersDeactivated && usersDeactivated.length > 0 ? (
									<div
										className={`${css.warningSummary} ${css.summaryHeader} ${css.deactivationInfo}`}
										onClick={() => setDisplayBulkDeactivateModal(true)}
									>
										<div className={css.exclamationIcon}>
											<FontAwesomeIcon icon={faUserTimes} />
										</div>
										<div>
											{`Deactivated ${usersDeactivated.length} user${
												usersDeactivated.length === 1 ? "" : "s"
											} from this sync`}
										</div>
										<div className={css.showMoreIcon}>
											<FontAwesomeIcon icon={faChevronRight} />
										</div>
									</div>
								) : null}

								{usersDeactivationDismissed ? (
									<div className={`${css.warningSummary} ${css.summaryHeader}`}>
										<div className={css.exclamationIcon}>
											<FontAwesomeIcon icon={faUserTimes} />
										</div>
										<div>
											{`Deactivating ${numUsersToDeactivate} user${
												numUsersToDeactivate === 1 ? "" : "s"
											} dismissed.`}
										</div>
									</div>
								) : null}

								{failed > 0 && logData.errors && logData.errors.length === 0 ? (
									<div className={`${css.warningSummary} ${css.summaryHeader}`}>
										<div className={css.exclamationIcon}>
											<FontAwesomeIcon icon={faExclamationCircle} />
										</div>
										<div>
											{`${failed} employee${
												failed === 1 ? "" : "s"
											} could not be updated due to an error.`}
										</div>
									</div>
								) : null}

								{updated > 0 ? (
									<div className={`${css.syncedSummary} ${css.summaryHeader}`}>
										<div className={css.checkIcon}>
											<FontAwesomeIcon icon={faCheckCircle} />
										</div>
										<div>{`Successfully synced ${updated} employee${
											updated === 1 ? "" : "s"
										}.`}</div>
									</div>
								) : null}

								{skipped ? (
									<div className={css.skippedSummary}>
										<div className={css.summaryHeader}>
											<div className={css.infoIcon}>
												<FontAwesomeIcon icon={faInfoCircle} />
											</div>
											<div>{`${noUpdates} employees had no updates to be made.`}</div>
										</div>
									</div>
								) : null}
							</div>
							<div>
								{logData?.errors && logData.errors.length > 0 ? (
									<div className={css.failedSummary}>
										<div className={css.summaryHeader}>
											<div className={css.timesIcon}>
												<FontAwesomeIcon icon={faTimesCircle} />
											</div>
											<div>sync was unsuccessful.</div>
										</div>
										<ul>
											{logData.errors.map((error) =>
												error?.type === "DUPLICATE_EMAIL_ERROR" ? (
													<React.Fragment key={error.email}>
														<li className={css.summaryDetail}>{error.email}:</li>
														{error.displayNames.map((displayName) => (
															<li
																key={displayName}
																className={css.duplicateEmailSummaryDetail}
															>
																{displayName}
															</li>
														))}
													</React.Fragment>
												) : (
													<li key={error} className={css.summaryDetail}>
														{error}
													</li>
												)
											)}
										</ul>
									</div>
								) : null}
								<div className={css.userUpdatesTableContainer}>
									<div>
										<div className={css.inputsContainer}>
											<Filters filters={filters} updateFilters={handleFilterChange} />
											<Search updateSearch={handleSearch} />
										</div>
									</div>
									<TableLite isLoading={isRequesting} columns={columns} rows={userUpdates} />
									{search.search && totalUsersUpdates < 1 ? (
										<div className={css.noneFound}>
											No user sync records found for search &apos;{search.search}&apos;
										</div>
									) : null}
									{totalUsersUpdates > 0 && totalUsersUpdates > PAGE_SIZE ? (
										<div className={css.paginatorWrapper}>
											<Paginator
												pageCount={Math.ceil(totalUsersUpdates / PAGE_SIZE)}
												currentPage={currentPage}
												onClick={handleChangePage}
											/>
										</div>
									) : null}
								</div>
							</div>
						</div>
					</div>

					{displayBulkDeactivateModal ? (
						<BulkDeactivateModal
							integrationLogID={logID}
							handleDismiss={handleDismiss}
							dismissingUsers={dismissingUsers}
							handleDeactivate={handleDeactivation}
							deactivatingUsers={deactivatingUsers}
							numUsersToDeactivate={
								usersDeactivated.length ? usersDeactivated.length : numUsersToDeactivate
							}
							displayDeactivate={usersDeactivationApproved !== null || usersDeactivated.length > 0}
							handleClose={() => setDisplayBulkDeactivateModal(false)}
							employeeData={employeeData}
							getEmployeeData={getDeactivatedEmployeeNames}
							employeesRequiringApprovalToDeactivate={employeesRequiringApprovalToDeactivate}
							getEmployeesRequiringApprovalToDeactivate={getEmployeesRequiringApprovalToDeactivate}
						/>
					) : null}
				</div>
				{data && !data.integrationLogs.length ? <ErrorPage /> : null}
			</div>
		</AdminContainer>
	);
};

export default LogDetails;
